import { useState } from 'react';
import { isPasskeySupported, loginWithPasskey, registerWithPasskey } from '../lib/passkey';
import { pushToast } from '../lib/toast';
import { toasts as copy, errors } from '@shared/copy';

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>('sign-in');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const supported = isPasskeySupported();

  async function handle() {
    if (busy) return;
    setBusy(true);
    try {
      if (mode === 'sign-up') {
        if (!email || !name) throw new Error('Email and name, please.');
        await registerWithPasskey(email.trim(), name.trim());
        pushToast(copy.signedUp, 'success');
      } else {
        await loginWithPasskey(email.trim() || undefined);
        pushToast(copy.signedIn, 'success');
      }
      onSignedIn();
    } catch (e: any) {
      pushToast(e?.message || errors.passkeyFailed, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full grid place-items-center px-4 py-10">
      <div className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-900/60 backdrop-blur p-6 shadow-xl">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-9 w-9 rounded-xl bg-amber-500 grid place-items-center text-black font-black">D</div>
          <div className="text-xl font-semibold tracking-tight">Doer</div>
        </div>
        <p className="text-sm text-zinc-400 mb-5">
          A todo app that actually expects you to do things.
        </p>

        <div className="flex rounded-lg bg-zinc-950 border border-zinc-800 p-1 mb-4 text-sm">
          <button
            className={`flex-1 rounded-md px-3 py-1.5 ${mode === 'sign-in' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400'}`}
            onClick={() => setMode('sign-in')}
          >
            Sign in
          </button>
          <button
            className={`flex-1 rounded-md px-3 py-1.5 ${mode === 'sign-up' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400'}`}
            onClick={() => setMode('sign-up')}
          >
            Sign up
          </button>
        </div>

        {mode === 'sign-up' && (
          <label className="block mb-3 text-sm">
            <span className="text-zinc-400">Name</span>
            <input
              className="mt-1 w-full rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 outline-none focus:border-amber-500"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What should we call you?"
            />
          </label>
        )}
        <label className="block mb-4 text-sm">
          <span className="text-zinc-400">Email {mode === 'sign-in' && <span className="text-zinc-600">(optional)</span>}</span>
          <input
            className="mt-1 w-full rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 outline-none focus:border-amber-500"
            autoComplete="username webauthn"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>

        <button
          onClick={handle}
          disabled={busy || !supported}
          className="w-full rounded-lg bg-amber-500 text-black font-medium py-2.5 disabled:opacity-50"
        >
          {busy ? 'Working on it…' : mode === 'sign-up' ? 'Create account with passkey' : 'Sign in with passkey'}
        </button>

        {!supported && (
          <p className="mt-3 text-xs text-red-400">
            Your browser doesn't support passkeys. Try a real one.
          </p>
        )}
        <p className="mt-4 text-xs text-zinc-500">
          No passwords. Just biometrics and a faint sense of obligation.
        </p>
      </div>
    </div>
  );
}
